import { zodResolver } from "@hookform/resolvers/zod";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { MessageCircle, Trash2, UserPlus, Users } from "lucide-react";
import { Suspense, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { Avatar, AvatarFallback } from "~/components/ui/avatar";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { CDNImage } from "~/components/ui/cdn-image";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { EmptyState } from "~/components/ui/empty-state";
import { Input } from "~/components/ui/input";
import { PageHeader } from "~/components/ui/page-title";
import { Skeleton } from "~/components/ui/skeleton";
import { findConversationWithUser } from "~/lib/functions/messaging";
import {
  contactsQueryOptions,
  useAddContact,
  useRemoveContact,
} from "~/lib/queries/contactsQuery";
import type { ContactInfo } from "~/lib/schemas/contacts";

export const Route = createFileRoute("/_user/contacts/")({
  staticData: { title: "Contacts" },
  head: () => ({
    title: "My Contacts | Dream Coach Match",
    meta: [
      {
        name: "description",
        content: "Manage your contacts on Dream Coach Match.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ContactsPage,
});

const addContactFormSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

type AddContactFormValues = z.infer<typeof addContactFormSchema>;

function ContactsPage(): React.JSX.Element {
  return (
    <div className="max-w-4xl space-y-8">
      <PageHeader
        title="Contacts"
        description="The people you can message on Dream Coach Match."
      />
      <AddContactSection />
      <Suspense fallback={<ContactsListSkeleton />}>
        <ContactsList />
      </Suspense>
    </div>
  );
}

function AddContactSection(): React.JSX.Element {
  const addContact = useAddContact();
  const form = useForm<AddContactFormValues>({
    resolver: zodResolver(addContactFormSchema),
    defaultValues: { email: "" },
  });

  function onSubmit(values: AddContactFormValues): void {
    addContact.mutate(
      { email: values.email },
      {
        onSuccess: (raw) => {
          const result = raw as {
            success: boolean;
            error?: string;
          };
          if (result.success) {
            toast.success("Contact added");
            form.reset();
          } else {
            form.setError("email", {
              message: result.error ?? "Failed to add contact",
            });
          }
        },
        onError: () => {
          toast.error("Something went wrong. Please try again.");
        },
      },
    );
  }

  return (
    <Card>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex items-start gap-4"
      >
        <div className="flex-1 space-y-1">
          <Input
            placeholder="Enter email address"
            {...form.register("email")}
            aria-invalid={!!form.formState.errors.email}
          />
          {form.formState.errors.email && (
            <p className="text-sm text-red-700">
              {form.formState.errors.email.message}
            </p>
          )}
        </div>
        <Button
          type="submit"
          disabled={addContact.isPending}
          className="shrink-0"
        >
          <UserPlus className="mr-2 h-4 w-4" />
          Add contact
        </Button>
      </form>
    </Card>
  );
}

function ContactsList(): React.JSX.Element {
  const { data } = useSuspenseQuery(contactsQueryOptions());
  const contacts = data.contacts;

  if (contacts.length === 0) {
    return <ContactsEmptyState />;
  }

  return (
    <Card className="gap-0 p-0">
      <ul className="divide-y divide-gray-100">
        {contacts.map((contact) => (
          <ContactRow key={contact.contactUserId} contact={contact} />
        ))}
      </ul>
    </Card>
  );
}

function ContactRow({ contact }: { contact: ContactInfo }): React.JSX.Element {
  const router = useRouter();
  const removeContact = useRemoveContact();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [messageLoading, setMessageLoading] = useState(false);

  const displayName = [contact.firstName, contact.lastName]
    .filter(Boolean)
    .join(" ");
  const initials = [contact.firstName?.[0], contact.lastName?.[0]]
    .filter(Boolean)
    .join("")
    .toUpperCase();

  async function handleMessage(): Promise<void> {
    setMessageLoading(true);
    try {
      const conversation = await findConversationWithUser({
        data: { userId: contact.contactUserId },
      });
      if (conversation) {
        router.navigate({
          to: "/messages/$conversationId",
          params: { conversationId: conversation.id },
        });
      } else {
        // No existing conversation — navigate to messages inbox
        router.navigate({ to: "/messages" });
      }
    } catch {
      toast.error("Could not open conversation");
    } finally {
      setMessageLoading(false);
    }
  }

  function handleRemove(): void {
    removeContact.mutate(
      { contactUserId: contact.contactUserId },
      {
        onSuccess: () => {
          toast.success("Contact removed");
          setConfirmOpen(false);
        },
        onError: () => {
          toast.error("Failed to remove contact");
        },
      },
    );
  }

  return (
    <>
      <li className="flex items-center gap-4 px-4 py-3">
        <Avatar className="h-10 w-10">
          {contact.profilePictureUrl ? (
            <CDNImage
              src={contact.profilePictureUrl}
              alt={displayName || "Contact"}
              width={40}
              height={40}
              className="h-full w-full rounded-full object-cover"
            />
          ) : (
            <AvatarFallback>{initials || "?"}</AvatarFallback>
          )}
        </Avatar>

        <div className="min-w-0 flex-1">
          <p className="truncate text-base font-medium text-gray-900">
            {displayName || "Unknown"}
          </p>
          {contact.email && (
            <p className="text-text-tertiary truncate text-sm">
              {contact.email}
            </p>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleMessage}
            disabled={messageLoading}
          >
            <MessageCircle className="mr-1.5 h-4 w-4" />
            Message
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setConfirmOpen(true)}
            className="text-red-700 hover:text-red-700"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </li>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove contact</DialogTitle>
          </DialogHeader>
          <p className="text-text-secondary text-sm">
            Are you sure you want to remove{" "}
            <span className="font-medium">{displayName || "this contact"}</span>
            ?
          </p>
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="default" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRemove}
              disabled={removeContact.isPending}
            >
              Remove
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ContactsEmptyState(): React.JSX.Element {
  return (
    <EmptyState
      icon={Users}
      title="No contacts yet"
      description="Add your first contact by entering their email address above. Stay connected with your coaches and fellow members."
    />
  );
}

function ContactsListSkeleton(): React.JSX.Element {
  return (
    <Card className="gap-0 p-0">
      <ul className="divide-y divide-gray-100">
        {Array.from({ length: 3 }, (_, i) => (
          <li key={i} className="flex items-center gap-4 px-4 py-3">
            <Skeleton className="size-10 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-48" />
            </div>
            <div className="flex gap-2">
              <Skeleton className="h-8 w-20" />
              <Skeleton className="h-8 w-8" />
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}
